import { useOrgMemberStore } from '@/stores/useOrgMemberStore';
import { useWorkspaceMemberStore } from '@/stores/useWorkspaceMemberStore';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';
import { pluginRegistry } from '@/plugins/pluginRegistry';
import {
  canAccessPlugin,
  isReadOnlyAccess,
  canUseFeature,
  isAtLeast,
} from '@/auth/roleUtils';
import type { RoleLevel } from '@/plugins/types';

interface CanAccessResult {
  canAccess: boolean;
  isReadOnly: boolean;
  isLoading: boolean;
}

interface UseCanAccessReturn {
  checkPlugin: (pluginId: string) => CanAccessResult;
  checkFeature: (pluginId: string, featureId: string) => boolean;
  hasMinOrgRole: (role: RoleLevel) => boolean;
  hasMinWorkspaceRole: (role: RoleLevel) => boolean;
  orgRole: string | null;
  workspaceRole: string | null;
  isLoading: boolean;
}

export function useCanAccess(): UseCanAccessReturn {
  const { currentUser } = useOrgMemberStore();
  const { members } = useWorkspaceMemberStore();
  const { activeWorkspace } = useWorkspaceStore();

  const orgRole = currentUser?.role ?? null;
  const isLoading = currentUser === null;

  const workspaceRole = (() => {
    if (!currentUser?.userId || !activeWorkspace) return null;
    const member = members.find((m) => m.userId === currentUser.userId);
    return member?.role ?? null;
  })();

  const checkPlugin = (pluginId: string): CanAccessResult => {
    const plugin = pluginRegistry.getPluginById(pluginId);
    if (!plugin) return { canAccess: false, isReadOnly: false, isLoading };

    return {
      canAccess: canAccessPlugin(plugin, orgRole, workspaceRole),
      isReadOnly: isReadOnlyAccess(plugin, orgRole),
      isLoading,
    };
  };

  const checkFeature = (pluginId: string, featureId: string): boolean => {
    const plugin = pluginRegistry.getPluginById(pluginId);
    if (!plugin) return false;
    return canUseFeature(plugin, featureId, orgRole, workspaceRole);
  };

  const hasMinOrgRole = (role: RoleLevel): boolean => {
    return isAtLeast(orgRole, role);
  };

  const hasMinWorkspaceRole = (role: RoleLevel): boolean => {
    return isAtLeast(workspaceRole, role);
  };

  return {
    checkPlugin,
    checkFeature,
    hasMinOrgRole,
    hasMinWorkspaceRole,
    orgRole,
    workspaceRole,
    isLoading,
  };
}
